import React, {
  useEffect, useRef, useState, useCallback,
} from 'react'
import { connect as connectRedux, useStore } from 'react-redux'
import { List, Map } from 'immutable'
import { selectCurrentUserProfilePicture } from '../../services/community/identity/profile/redux/selectors'
import { getBoundActions } from '../../actions/index'
import Button, { TYPES } from '../Button/index'
import ButtonSignUp, { BUTTON_SIGN_UP_TYPE_BUTTON } from '../ButtonSignUp/index'
import { TYPE_LOGIN } from '../../services/dialog/index'
import { isBrowser } from '../../config/environment'
import useCommunityEnabled from '../../services/community/shared/useCommunityEnabled'
import LiveAccessSchedule from '../LiveAccessSchedule/LiveAccessSchedule'
import { getChatInfo } from '../LivePage/LiveExperience'
import Chat, { MAIN_CHAT_CHANNEL_POSTFIX, TECH_CHAT_CHANNEL_POSTFIX } from '../EventsPage/Chat/index'
import { scheduleKey, trackKey } from '../../services/live-access-events/reducers'
import { historyRedirect, HISTORY_METHOD_REPLACE } from '../../services/navigation/index'
import LiveVideoPlayer from '../LiveVideoPlayer/index'
import { getSessionTimeout, isInSession } from '../../services/live-access-events/index'
import { getLocalStorage, setLocalStorage } from '../../services/local-preferences/index'
import EmailCapture from './EmailCapture'

const SIGN_UP_CTA_TIMEOUT = 60 * 1000 * 60 * 2
const TIME_USED_INTERVAL = 60 * 1000
const GaiaStreamTrackId = 'e1ac4473-89eb-40ca-921e-d85cecd4aa0b'
const GaiaLiveChannel = 'Live - Gaia-Linear-Stream'
const EmailCaptureKey = 'Live-Channel'
const GNFreeTimeUsedKey = 'GN-FreeTimeUsed'
const BECOMING_SUPERNATURAL_EVENT_ID = 'e3294a4c-7ae0-42f9-b28a-5b3b0c35eec1'
const BUTTON_CLASS = ['button--primary']
function renderSignup (staticText) {
  return (
    <div className="live-channel-page-sign-up-cta">
      <div className="live-channel-page-sign-up-cta__text">
        {staticText.getIn(['data', 'ctaText'])}
      </div>
      <ButtonSignUp
        type={BUTTON_SIGN_UP_TYPE_BUTTON}
        buttonClass={BUTTON_CLASS}
        text={staticText.getIn(['data', 'ctaButtonText'])}
      />
    </div>
  )
}

function isEmailCaptured (emailSignup, resolver) {
  if (isBrowser()) {
    const captured = getLocalStorage(EmailCaptureKey) === 'yes'
      || emailSignup.get('success') === true
    const haveEmail = resolver.getIn(['query', 'haveEmail']) === 'true'
    return captured || haveEmail
  }
  return false
}

function LiveChannelPage ({
  setLiveAccessScheduleVisible,
  getLiveAccessSessionDetails,
  setOverlayDialogVisible,
  getLiveAccessSchedule,
  getLiveAccessTrack,
  livePlaybackUrl,
  scheduleVisible,
  getChatList,
  emailSignup,
  chatEnabled,
  chatChannel,
  staticText,
  sessionId,
  schedule,
  chatInfo,
  resolver,
  history,
  location,
  currentUserPicture,
  user,
  auth,
}) {
  const { enabled: communityEnabled } = useCommunityEnabled()
  const isAnonymous = !auth.get('jwt')
  const username = auth.get('username')
  const language = user.getIn(['data', 'language', 0], 'en')
  const imageUrl = communityEnabled && currentUserPicture
    ? currentUserPicture.small
    : user.getIn(['data', 'profile', 'picture', 'hdtv_190x266'])
  const hasEntitlements = auth.get('subscriptions') && auth.get('subscriptions').size > 0
  const showEmailCapture = (isAnonymous || !hasEntitlements)
    && !isEmailCaptured(emailSignup, resolver)

  const store = useStore()
  const timeoutRef = useRef()
  const signUpCTATimeoutRef = useRef()
  const [nowTs, setNowTs] = useState((new Date()).getTime())
  const [timeUsed, setTimeUsed] = useState(getLocalStorage(GNFreeTimeUsedKey) || 0)

  const onRefresh = useCallback(() => (
    getLiveAccessTrack({ id: GaiaStreamTrackId, language })
  ), [GaiaStreamTrackId, language])

  const showOverlay = useCallback(() => (
    setOverlayDialogVisible(TYPE_LOGIN)
  ), [])

  const showSchedule = useCallback(() => (
    setLiveAccessScheduleVisible(false)
  ), [])

  useEffect(() => {
    getChatList(BECOMING_SUPERNATURAL_EVENT_ID)
    return () => {
      clearTimeout(timeoutRef.current)
      clearInterval(signUpCTATimeoutRef.current)
    }
  }, [])

  useEffect(() => {
    getLiveAccessTrack({ id: GaiaStreamTrackId, language })
    getLiveAccessSchedule({ trackId: GaiaStreamTrackId, language })
  }, [language])

  useEffect(() => {
    if (sessionId) {
      getLiveAccessSessionDetails({ id: sessionId, language })
    }
  }, [sessionId])

  // this effect is exceptionally bad practice
  // but I do not have the time to convert all of this over
  // to using a watcher. There was a LARGE bug in here causing
  // the timeout to execute immediately, over and over,
  // which makes the entire page re-render constantly.
  useEffect(() => {
    const scheduleJS = schedule && schedule.size ? schedule.toJS() : null
    const sessionTimeout = getSessionTimeout(scheduleJS, nowTs) + 1000
    const delay = sessionTimeout > 2147483647 ? 2147483647 : sessionTimeout

    clearTimeout(timeoutRef.current)

    if (scheduleJS && !isInSession(scheduleJS, nowTs)) {
      historyRedirect({
        historyMethod: HISTORY_METHOD_REPLACE,
        url: '/',
        language,
        history,
        auth,
      })
      return
    }
    timeoutRef.current = setTimeout(() => {
      setNowTs((new Date()).getTime())
    }, delay)
  }, [nowTs, schedule])

  useEffect(() => {
    clearInterval(signUpCTATimeoutRef.current)
    if (isAnonymous || !hasEntitlements) {
      signUpCTATimeoutRef.current = setInterval(() => {
        setTimeUsed((curTimeUsed) => curTimeUsed + TIME_USED_INTERVAL)
      }, TIME_USED_INTERVAL)
    }
  }, [isAnonymous, hasEntitlements])

  useEffect(() => {
    setLocalStorage(GNFreeTimeUsedKey, timeUsed, auth ? auth.get('uid') : -1, auth)
  }, [timeUsed])

  return (
    <div className="live-channel-page">
      <div className={`live-channel-player-and-schedule ${chatEnabled ? '' : 'live-channel-player-and-schedule--full-height'}`}>
        <div className="live-channel-player">
          {livePlaybackUrl && !showEmailCapture ? (
            <LiveVideoPlayer
              playerSrc={livePlaybackUrl}
              title={GaiaLiveChannel}
              location={location}
              history={history}
              store={store}
            />
          ) : null}
          {timeUsed > SIGN_UP_CTA_TIMEOUT ? (
            <div className="live-channel-player__video-overlay live-channel-player__video-overlay--blocking">
              {renderSignup(staticText)}
            </div>
          ) : null}
        </div>
        <LiveAccessSchedule
          buttonText={staticText.getIn(['data', 'seeFullSchedule'])}
          header={staticText.getIn(['data', 'schedule'])}
          closeHandler={showSchedule}
          visible={scheduleVisible}
          sessionList={schedule}
          language={language}
          nowTs={nowTs}
          full
        />
      </div>
      {showEmailCapture ? (
        <div className="live-channel-player__video-overlay live-channel-player__video-overlay--blocking">
          <div className="live-channel-page__email-capture">
            <EmailCapture captureKey={EmailCaptureKey} />
            <div className="live-channel-page__email-capture__label">
              {staticText.getIn(['data', 'loginLabel'])}
            </div>
            <Button
              type={TYPES.PRIMARY}
              onClick={showOverlay}
            >
              {staticText.getIn(['data', 'login'])}
            </Button>
          </div>
        </div>
      ) : null}
      <div className={[`live-channel-page__chat ${chatEnabled ? '' : 'live-channel-page__chat--hidden'}`]}>
        {chatChannel && chatInfo.pubNubSubscribeKey
          ? <Chat
            pubNubMainChannel={chatChannel + MAIN_CHAT_CHANNEL_POSTFIX}
            pubNubTechChannel={chatChannel + TECH_CHAT_CHANNEL_POSTFIX}
            joinChatText={staticText.getIn(['data', 'joinChat'])}
            pubNubSubscribeKey={chatInfo.pubNubSubscribeKey}
            pubNubPublishKey={chatInfo.pubNubPublishKey}
            username={hasEntitlements && username}
            testing={global.BROWSER_TEST}
            staticText={staticText}
            onRefresh={onRefresh}
            imageUrl={imageUrl}
          />
          : `chatInfo for this event is missing!  ${chatInfo.toString()}`
        }
      </div>
    </div>
  )
}

export default connectRedux(
  (state) => {
    const {
      user, auth, resolver, emailSignup, staticText, liveAccessEvents,
    } = state
    const language = user.getIn(['data', 'language', 0], 'en')
    const track = liveAccessEvents.getIn([trackKey, GaiaStreamTrackId, language, 'data'])
    const schedule = liveAccessEvents.getIn([scheduleKey, GaiaStreamTrackId, 'data', 'schedule'], List())
    const sessionId = schedule && schedule.getIn([0, 'id'])
    return {
      livePlaybackUrl: liveAccessEvents.getIn(['sessionDetails', sessionId, language, 'data', 'session', 'playbackUrl']),
      chatInfo: getChatInfo(liveAccessEvents.getIn(['chatList', BECOMING_SUPERNATURAL_EVENT_ID, 'data', 'chatInfo'])),
      currentUserPicture: selectCurrentUserProfilePicture(state),
      scheduleVisible: liveAccessEvents.getIn([scheduleKey, 'visible']),
      staticText: staticText.getIn(['data', 'liveChannelPage'], Map()),
      chatEnabled: track && track.get('liveStreamActive'),
      chatChannel: track && track.get('channelId'),
      emailSignup,
      sessionId,
      resolver,
      schedule,
      track,
      user,
      auth,
    }
  },
  (dispatch) => {
    const actions = getBoundActions(dispatch)
    return {
      setLiveAccessScheduleVisible: actions.liveAccessEvents.setLiveAccessScheduleVisible,
      getLiveAccessSessionDetails: actions.liveAccessEvents.getLiveAccessSessionDetails,
      getLiveAccessSchedule: actions.liveAccessEvents.getLiveAccessSchedule,
      getLiveAccessTrack: actions.liveAccessEvents.getLiveAccessTrack,
      setOverlayDialogVisible: actions.dialog.setOverlayDialogVisible,
      getChatList: actions.liveAccessEvents.getLiveAccessChatList,
      setPageSeo: actions.page.setPageSeo,
    }
  },
)(LiveChannelPage)
